import { useQuery } from '@tanstack/react-query';
import { api } from '../../../../singleton/axios';
import { useTourMode } from '../../../../contexts/TourModeContext';

export interface IncomeStatementRecord {
  symbol: string;
  date: string;
  epsActual: number | null;
  epsEstimated: number | null;
  revenueActual: number | null;
  revenueEstimated: number | null;
  ebitdaActual: number | null;
  netIncomeActual: number | null;
  researchAndDevelopmentExpensesActual: number | null;
  generalAndAdministrativeExpensesActual: number | null;
  sellingAndMarketingExpensesActual: number | null;
  sellingGeneralAndAdministrativeExpensesActual: number | null;
  otherExpensesActual: number | null;
  operatingExpensesActual: number | null;
  grossProfitActual: number | null;
  operatingIncomeActual: number | null;
  lastUpdated: string;
}

export const useCompanyIncomeStatement = (
  ticker: string | undefined,
  limit?: number,
  period?: 'annual' | 'quarter' | 'ttm'
) => {
  const { isTourMode, getMockData } = useTourMode();

  return useQuery<IncomeStatementRecord[], Error>({
    queryKey: ['companyIncomeStatement', ticker, limit, period, isTourMode],
    queryFn: async () => {
      // Return mock data in tour mode
      if (isTourMode) {
        // The tour fixture contains quarterly statements, not TTM statements.
        if (period === 'ttm') return [];
        const mockData = getMockData<IncomeStatementRecord[]>('incomeStatement');
        if (mockData) return mockData;
      }

      if (!ticker) {
        throw new Error('Ticker is required');
      }

      const params: Record<string, string> = { ticker };
      if (limit) {
        params.limit = limit.toString();
      }
      if (period) {
        params.period = period;
      }

      const { data } = await api.get<IncomeStatementRecord[]>('/company-income-statement', {
        params,
      });
      return data;
    },
    enabled: isTourMode || !!ticker,
    refetchOnWindowFocus: false,
    staleTime: isTourMode ? Infinity : 2.5 * 60 * 1000, // Never stale in tour mode
    retry: (failureCount, error) => {
      if (isTourMode) return false;
      // Don't retry on 404s - legitimately means no income statement data exists
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      if ((error as any)?.response?.status === 404) {
        return false;
      }
      // Retry other errors (500s, network issues) max 2 times
      return failureCount < 2;
    },
  });
};
